import { Table, TableBody, TableCell, TableRow } from "@mui/material";
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import PATEditButton from "../../components/Buttons/PATEditButton";
import PATDeleteButton from "../../components/Buttons/PATDeleteButton";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useDispatch, useSelector } from "react-redux";
import { updateOrCreateUserSetting } from "../../reducers/UserSettingsReducer";
import {
	getRowsPerPageOptions,
	StatusBadge,
	formatDate,
	calculateDownTime,
	formatCurrency,
	getCodeId,
} from "../../utils/functions";
import { Box } from "@mui/system";
import { useTheme } from "@emotion/react";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { AMSModules, paginationStyle } from "../../utils/constants";
import WorkOrdersTableToolbar from "./WorkOrdersTableToolbar";
import AddEditWorkOrderDialog from "./AddEditWorkOrderDialog";
import PATReopenButton from "../../components/Buttons/PATReopenButton";
import PATCloseButton from "../../components/Buttons/PATCloseButton";
import {
	getWorkOrdersList,
	createWorkOrder,
	updateWorkOrder,
	deleteWorkOrder,
	closeWorkOrder,
	reopenWorkOrder,
} from "../../reducers/WorkOrdersReducer";
import { notify } from "../../reducers/NotifierReducer";
import DeleteConfirmationDialog from "../../components/DeleteConfirmationDialog";
import { getEmployees, getFundingSources } from "../../utils/commonServices";
import {
	getVehicleOptions,
	getVendorOptions,
	getWorkOrderTypeOptions,
	getWorkOrderStatusOptions,
} from "./useWorkOrdersData";
import CloseWorkOrderDialog from "./CloseWorkOrderDialog";

const INITIAL_PAGE = 0;
const INITIAL_PAGE_SIZE = 20;
const INITIAL_SORT_MODEL = [{ field: "id", sort: "desc" }];

export const initialFilterState = {
	status: null,
	type: [],
	vehicleId: [],
	vendor: [],
	perPersonnelId: [],
	fundingSourceId: [],
	period: null,
	from: null,
	to: null,
};

const WorkOrders = () => {
	const history = useHistory();
	const dispatch = useDispatch();
	const theme = useTheme();

	const { workOrders, loading, total } = useSelector(
		(state) =>
			state?.workOrders || { workOrders: [], loading: false, total: 0 },
	);
	const userAgency = useSelector((state) => state.user.userAgency);
	const { userData, isAuthenticated } = useSelector((state) => state.user);
	const { userSettings } = useSelector((state) => state.userSettings);
	const themeMode = useSelector((state) => state.theme.mode);
	const filtersInfo = useSelector((state) => state.filtersInfo);
	const dictionary = useSelector((state) => state.dictionary.dictionary);

	const {
		codePerWorkOrderTypes,
		codePerWorkOrderStatus,
		codePerVehicleStatus,
		codePerVehicleServiceRequirements: serviceRequirements,
	} = dictionary || {};

	const [search, setSearch] = useState("");
	const [openDialog, setOpenDialog] = useState(false);
	const [columnsOrder, setColumnsOrder] = useState([]);
	const [columnsSize, setColumnsSize] = useState([]);
	const [settingsLoaded, setSettingsLoaded] = useState(false);
	const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
	const [page, setPage] = useState(INITIAL_PAGE);
	const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
	const [sortModel, setSortModel] = useState(INITIAL_SORT_MODEL);
	const [filters, setFilters] = useState(
		filtersInfo.workOrders || initialFilterState,
	);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [openCloseDialog, setOpenCloseDialog] = useState(false);
	const [selectedId, setSelectedId] = useState(null);
	const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
	const [employees, setEmployees] = useState([]);
	const [fundingSources, setFundingSources] = useState([]);
	const [vehicleOptions, setVehicleOptions] = useState([]);
	const [vendorOptions, setVendorOptions] = useState([]);
	const [typeOptions, setTypeOptions] = useState([]);
	const [statusOptions, setStatusOptions] = useState([]);
	const [vehicleStatusOptions, setVehicleStatusOptions] = useState([]);

	const currentAgency = useRef(null);

	const handleLoadData = async () => {
		const [empList, fundingList, vehicles, vendors, types, statuses] =
			await Promise.all([
				getEmployees(userAgency, true),
				getFundingSources(userAgency, true),
				getVehicleOptions(userAgency),
				getVendorOptions(userAgency),
				getWorkOrderTypeOptions(dictionary),
				getWorkOrderStatusOptions(dictionary),
			]);

		// Vehicle status options
		const vehicleStatuses =
			codePerVehicleStatus?.map((status) => ({
				label: status?.Code,
				value: status?.Code,
				ptsCodeId: status?.ptsCodeId,
			})) || [];

		setEmployees(empList);
		setFundingSources(fundingList);
		setVehicleOptions(vehicles);
		setVendorOptions(vendors);
		setTypeOptions(types);
		setStatusOptions(statuses);
		setVehicleStatusOptions(vehicleStatuses);
	};

	useEffect(() => {
		if (userAgency && dictionary) {
			handleLoadData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userAgency, dictionary]);

	useEffect(() => {
		if (!settingsLoaded || !isAuthenticated) return;

		const { field, sort } = sortModel[0] || {};
		if (field && sort) {
			const isAgencyChanged = currentAgency.current !== userAgency;
			if (isAgencyChanged) {
				resetFilters();
				currentAgency.current = userAgency;
			}

			dispatch(
				getWorkOrdersList(
					page,
					pageSize,
					field,
					sort,
					search,
					isAgencyChanged ? initialFilterState : filters,
					userAgency,
				),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		dispatch,
		page,
		pageSize,
		sortModel,
		search,
		filters,
		settingsLoaded,
		isAuthenticated,
		userAgency,
	]);

	// Get vehicle info for display
	const getVehicleDisplay = useCallback(
		(vehicleId) => {
			const vehicle = vehicleOptions.find((v) => v.value === vehicleId);
			return vehicle ? vehicle.label : vehicleId || "—";
		},
		[vehicleOptions],
	);

	// Get employee info for display
	const getEmployeeDisplay = useCallback(
		(employeeId) => {
			const employee = employees.find(
				(e) => e.value === employeeId || e.value === String(employeeId),
			);
			return employee ? employee.label : "—";
		},
		[employees],
	);

	const baseColumns = useMemo(
		() => [
			{
				field: "id",
				headerName: "WO#",
				width: 100,
				sortable: true,
				renderCell: ({ row }) => {
					const woNum = String(row.id).slice(-4).padStart(4, "0");
					return <span>WO-{woNum}</span>;
				},
			},
			{
				field: "vehicleId",
				headerName: "Vehicle",
				width: 200,
				sortable: true,
				renderCell: ({ row }) => getVehicleDisplay(row.vehicleId),
			},
			{
				field: "type",
				headerName: "Type",
				width: 150,
				sortable: true,
				renderCell: ({ row }) => {
					// Use typeInfo if available from association
					return row?.typeInfo?.Code || row?.type || "—";
				},
			},
			{
				field: "status",
				headerName: "Status",
				width: 120,
				sortable: true,
				renderCell: ({ row }) => {
					const statusValue = row?.statusInfo?.Code || row?.status;
					return <StatusBadge status={statusValue} />;
				},
			},
			{
				field: "perPersonnelId",
				headerName: "Assigned To",
				width: 180,
				sortable: true,
				renderCell: ({ row }) => getEmployeeDisplay(row.perPersonnelId),
			},
			{
				field: "vendor",
				headerName: "Vendor",
				width: 150,
				sortable: true,
				renderCell: ({ row }) => row?.vendor || "—",
			},
			{
				field: "openedDate",
				headerName: "Opened",
				width: 120,
				sortable: true,
				renderCell: ({ row }) => formatDate(row?.openedDate),
			},
			{
				field: "closedDate",
				headerName: "Closed",
				width: 120,
				sortable: true,
				renderCell: ({ row }) =>
					row?.closedDate ? formatDate(row?.closedDate) : "—",
			},
			{
				field: "downTime",
				headerName: "Down Time",
				width: 100,
				sortable: false,
				renderCell: ({ row }) => {
					if (!row?.openedDate) return "—";
					const endDate = row?.closedDate || new Date();
					return calculateDownTime(row.openedDate, endDate);
				},
			},
			{
				field: "cost",
				headerName: "Cost",
				width: 100,
				sortable: true,
				renderCell: ({ row }) =>
					row?.cost ? formatCurrency(row.cost) : "—",
			},
			{
				field: "odometerAtService",
				headerName: "Odometer",
				width: 100,
				sortable: true,
				renderCell: ({ row }) =>
					row?.odometerAtService
						? `${row.odometerAtService.toLocaleString()} mi`
						: "—",
			},
			{
				field: "actions",
				headerName: "Actions",
				type: "actions",
				width: 150,
				align: "center",
				sortable: false,
				renderCell: ({ row }) =>
					row.id ? (
						<Table>
							<TableBody>
								<TableRow>
									<TableCell
										align="right"
										sx={{ padding: 0.5 }}
									>
										{(row?.statusInfo?.Code ||
											row?.status) === "Open" ? (
											<PATCloseButton
												tooltipText="Close Work Order"
												onClick={() => {
													setSelectedWorkOrder(row);
													setOpenCloseDialog(true);
												}}
												size="20px"
											/>
										) : (
											<PATReopenButton
												tooltipText="Reopen Work Order"
												onClick={() =>
													handleReopenWorkOrder(
														row.id,
														row,
													)
												}
												size="20px"
											/>
										)}
									</TableCell>
									<TableCell
										align="right"
										sx={{ padding: 0.5 }}
									>
										<PATEditButton
											tooltipText="Edit Work Order"
											onClick={() => {
												setSelectedWorkOrder(row);
												setOpenDialog(true);
											}}
											size="20px"
										/>
									</TableCell>
									<TableCell
										align="right"
										sx={{ padding: 0.5 }}
									>
										<PATDeleteButton
											tooltipText="Delete Work Order"
											onClick={() => {
												setSelectedId(row.id);
												setSelectedWorkOrder(row);
												setOpenDeleteDialog(true);
											}}
											size="20px"
										/>
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					) : null,
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[history, getVehicleDisplay, getEmployeeDisplay],
	);

	const columns = useMemo(() => {
		const hasOrder = columnsOrder.length > 0;
		const hasSizes = Object.keys(columnsSize).length > 0;

		if (!hasOrder && !hasSizes) return baseColumns;

		let baseOrderedColumns;

		if (hasOrder) {
			const ordered = columnsOrder
				.map((field) => baseColumns.find((col) => col.field === field))
				.filter(Boolean);

			const remaining = baseColumns.filter(
				(col) => !columnsOrder.includes(col.field),
			);

			const actionsIndex = ordered.findIndex(
				(col) => col.field === "actions",
			);
			const insertAt = actionsIndex > -1 ? actionsIndex : ordered.length;

			baseOrderedColumns = [
				...ordered.slice(0, insertAt),
				...remaining,
				...ordered.slice(insertAt),
			];
		} else {
			baseOrderedColumns = baseColumns;
		}

		const finalColumns = baseOrderedColumns.map((col) => ({
			...col,
			width: columnsSize[col.field] ?? col.width ?? 100,
		}));

		return finalColumns;
	}, [baseColumns, columnsOrder, columnsSize]);

	const allColumnFields = baseColumns.map((col) => col.field);

	useEffect(() => {
		const defaultVisibility = {};
		currentAgency.current = userAgency;
		allColumnFields.forEach((field) => {
			defaultVisibility[field] = true;
		});
		setColumnVisibilityModel((prev) => ({ ...defaultVisibility, ...prev }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!isAuthenticated || settingsLoaded) return;

		const initialize = async () => {
			const settingsMap = Object.fromEntries(
				userSettings.map((s) => [s.key, s.value]),
			);
			if (settingsMap["workOrdersTableColumnOrder"]) {
				setColumnsOrder(
					JSON.parse(settingsMap["workOrdersTableColumnOrder"]),
				);
			}

			if (settingsMap["workOrdersTableColumnSizes"]) {
				const widthArray = JSON.parse(
					settingsMap["workOrdersTableColumnSizes"],
				);
				const result = widthArray.reduce((acc, curr) => {
					acc[curr.field] = curr.width;
					return acc;
				}, {});
				setColumnsSize(result);
			}

			if (settingsMap["workOrdersTableColumnVisibility"]) {
				setColumnVisibilityModel(
					JSON.parse(settingsMap["workOrdersTableColumnVisibility"]),
				);
			}

			if (settingsMap["workOrdersTableSortModel"]) {
				const savedSortModel = JSON.parse(
					settingsMap["workOrdersTableSortModel"],
				);
				if (Array.isArray(savedSortModel)) {
					const validSortModel = savedSortModel.filter((model) =>
						baseColumns.some((col) => col.field === model.field),
					);
					if (validSortModel.length > 0) {
						setSortModel(savedSortModel);
					}
				}
			}
			setSettingsLoaded(true);
		};

		initialize();
	}, [isAuthenticated, userSettings, settingsLoaded, baseColumns]);

	const handleSortModelChange = useCallback(
		(model) => {
			const newSortModel = model.length > 0 ? model : INITIAL_SORT_MODEL;

			const isSortModelDifferent =
				JSON.stringify(newSortModel) !== JSON.stringify(sortModel);

			if (isSortModelDifferent) {
				setSortModel(newSortModel);
				setPage(INITIAL_PAGE);

				if (userData?.user?.ptsUserID) {
					dispatch(
						updateOrCreateUserSetting(
							userData.user.ptsUserID,
							"workOrdersTableSortModel",
							JSON.stringify(newSortModel),
						),
					);
				}
			}
		},
		[sortModel, dispatch, userData?.user?.ptsUserID],
	);

	const handleColumnOrderChange = useCallback(
		(params, event, details) => {
			const newColumnOrder = details.api
				.getAllColumns()
				.map((col) => col.field);
			if (userData?.user?.ptsUserID) {
				dispatch(
					updateOrCreateUserSetting(
						userData.user.ptsUserID,
						"workOrdersTableColumnOrder",
						JSON.stringify(newColumnOrder),
					),
				);
			}
		},
		[dispatch, userData?.user?.ptsUserID],
	);

	const handleColumnWidthChange = (params, event, details) => {
		const columnSizes = details.api.getAllColumns().map((col) => ({
			field: col.field,
			width: col.width,
		}));
		if (userData?.user?.ptsUserID) {
			dispatch(
				updateOrCreateUserSetting(
					userData.user.ptsUserID,
					"workOrdersTableColumnSizes",
					JSON.stringify(columnSizes),
				),
			);
		}
	};

	const handleColumnVisibilityModelChange = useCallback(
		(newModel) => {
			setColumnVisibilityModel(newModel);
			if (userData?.user?.ptsUserID) {
				dispatch(
					updateOrCreateUserSetting(
						userData.user.ptsUserID,
						"workOrdersTableColumnVisibility",
						JSON.stringify(newModel),
					),
				);
			}
		},
		[dispatch, userData?.user?.ptsUserID],
	);

	const rowsPerPageOptions = useMemo(
		() => getRowsPerPageOptions(total),
		[total],
	);

	const handleOnFilterChange = (newFilters) => {
		setFilters(newFilters);
		dispatch({ type: AMSModules.WorkOrders, payload: newFilters });
	};

	const resetFilters = () => {
		setFilters(initialFilterState);
		dispatch({ type: AMSModules.WorkOrders, payload: initialFilterState });
	};

	const handleSaveWorkOrder = async (payload) => {
		try {
			// Get typeId and statusId from dictionary
			const typeId = getCodeId(payload.type, codePerWorkOrderTypes);
			const statusId = getCodeId(payload.status, codePerWorkOrderStatus);

			const data = {
				...payload,
				typeId,
				statusId,
			};

			if (selectedWorkOrder) {
				await dispatch(
					updateWorkOrder(
						selectedWorkOrder.id,
						data,
						userAgency,
						userData.user.ptsUserID,
						selectedWorkOrder,
					),
				);
			} else {
				await dispatch(
					createWorkOrder(data, userAgency, userData.user.ptsUserID),
				);
			}

			setOpenDialog(false);
			setSelectedWorkOrder(null);

			// Refresh vendor options in case a new vendor was added
			const vendors = await getVendorOptions(userAgency);
			setVendorOptions(vendors);

			dispatch(notify("Work order saved successfully", "success"));
		} catch (error) {
			dispatch(notify("Failed to save work order", "error"));
		}
	};

	const handleCloseWorkOrder = async (
		closedDate,
		newVehicleStatus,
		odometerAtService,
	) => {
		try {
			await dispatch(
				closeWorkOrder(
					selectedWorkOrder.id,
					closedDate,
					selectedWorkOrder.vehicleId,
					newVehicleStatus,
					odometerAtService,
					userData.user.ptsUserID,
					userAgency,
					selectedWorkOrder,
				),
			);
			setOpenCloseDialog(false);
			setSelectedWorkOrder(null);
			dispatch(notify("Work order closed successfully", "success"));
		} catch (error) {
			dispatch(notify("Failed to close work order", "error"));
		}
	};

	const handleReopenWorkOrder = async (id, oldData) => {
		try {
			await dispatch(
				reopenWorkOrder(
					id,
					userData.user.ptsUserID,
					userAgency,
					oldData,
				),
			);
			dispatch(notify("Work order reopened successfully", "success"));
		} catch (error) {
			dispatch(notify("Failed to reopen work order", "error"));
		}
	};

	const handleDelete = async () => {
		try {
			await dispatch(
				deleteWorkOrder(
					selectedId,
					userData.user.ptsUserID,
					userAgency,
					selectedWorkOrder,
				),
			);
			setOpenDeleteDialog(false);
			setSelectedId(null);
			setSelectedWorkOrder(null);
			dispatch(notify("Work order deleted successfully", "success"));
		} catch (error) {
			dispatch(notify("Failed to delete work order", "error"));
		}
	};

	// Prepare print data
	const printWorkOrders = useMemo(() => {
		return workOrders.map((wo) => ({
			...wo,
			woNumber: `WO-${String(wo.id).slice(-4).padStart(4, "0")}`,
			vehicle: getVehicleDisplay(wo.vehicleId),
			assignedTo: getEmployeeDisplay(wo.perPersonnelId),
			typeDisplay: wo?.typeInfo?.Code || wo?.type || "—",
			statusDisplay: wo?.statusInfo?.Code || wo?.status || "—",
			openedDateFormatted: formatDate(wo.openedDate),
			closedDateFormatted: wo.closedDate
				? formatDate(wo.closedDate)
				: "—",
			costFormatted: wo.cost ? formatCurrency(wo.cost) : "—",
			odometerFormatted: wo.odometerAtService
				? `${wo.odometerAtService.toLocaleString()} mi`
				: "—",
			downTime: wo.openedDate
				? calculateDownTime(wo.openedDate, wo.closedDate || new Date())
				: "—",
		}));
	}, [workOrders, getVehicleDisplay, getEmployeeDisplay]);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 85px)",
				marginTop: theme.spacing(0.5),
			}}
		>
			<WorkOrdersTableToolbar
				onAddClick={() => {
					setSelectedWorkOrder(null);
					setOpenDialog(true);
				}}
				vehicleOptions={vehicleOptions}
				vendorOptions={vendorOptions}
				typeOptions={typeOptions}
				employeeOptions={employees}
				fundingOptions={fundingSources}
				statusOptions={statusOptions}
				search={search}
				setSearch={setSearch}
				workOrders={printWorkOrders}
				resetFilters={resetFilters}
				filters={filters}
				onFilterChange={handleOnFilterChange}
			/>

			<Box
				sx={{
					flex: 1,
					overflow: "hidden",
					display: "flex",
					flexDirection: "column",
				}}
			>
				<DataGridPro
					rows={workOrders}
					columns={columns}
					initialState={{ pinnedColumns: { right: ["actions"] } }}
					getRowId={(row) => row.GUID || row.id}
					columnVisibilityModel={columnVisibilityModel}
					onColumnVisibilityModelChange={
						handleColumnVisibilityModelChange
					}
					pageSize={pageSize}
					rowsPerPageOptions={rowsPerPageOptions}
					pagination
					paginationMode="server"
					sortModel={sortModel}
					sortingMode="server"
					onSortModelChange={handleSortModelChange}
					rowCount={total}
					page={page}
					onPageChange={setPage}
					onPageSizeChange={setPageSize}
					loading={loading}
					disableSelectionOnClick
					onColumnOrderChange={handleColumnOrderChange}
					onColumnWidthChange={handleColumnWidthChange}
					sx={{
						...paginationStyle(theme, themeMode),
						color: theme.palette.colorFont,
						margin: 1,
					}}
				/>
			</Box>

			<AddEditWorkOrderDialog
				open={openDialog}
				onClose={() => {
					setOpenDialog(false);
					setSelectedWorkOrder(null);
				}}
				onSave={handleSaveWorkOrder}
				initialData={selectedWorkOrder}
				vehicleOptions={vehicleOptions}
				workOrderTypes={typeOptions}
				statusOptions={statusOptions}
				employeeOptions={employees}
				fundingSourceOptions={fundingSources}
				vehicleStatusOptions={vehicleStatusOptions}
				serviceRequirements={serviceRequirements}
			/>

			<DeleteConfirmationDialog
				open={openDeleteDialog}
				onClose={() => {
					setOpenDeleteDialog(false);
					setSelectedWorkOrder(null);
					setSelectedId(null);
				}}
				onConfirm={handleDelete}
				title="Delete Work Order"
				message="Are you sure you want to delete this work order?"
			/>

			<CloseWorkOrderDialog
				open={openCloseDialog}
				onClose={() => {
					setOpenCloseDialog(false);
					setSelectedWorkOrder(null);
				}}
				onConfirm={handleCloseWorkOrder}
				vehicleOptions={vehicleOptions}
				workOrder={selectedWorkOrder}
				vehicleStatusOptions={vehicleStatusOptions}
			/>
		</Box>
	);
};

export default WorkOrders;
